// src/utils/ledger/index.js
// ============================================================
//  UNIFIED LEDGER REPORT API
//  Single entry point — auto-selects tier based on client config.
//  Used throughout Development Express frontend.
// ============================================================
import { generateStandardLedger } from './generateStandardLedger';
import { generatePremiumLedger } from './generatePremiumLedger';

/**
 * Generate Internal Advance Ledger Report.
 *
 * @param {Object} options
 * @param {Object} options.client       - Client object with report_tier field
 * @param {Object} options.period       - { start, end } ISO date strings
 * @param {Array}  options.transactions - Transaction rows from Supabase
 * @param {Array}  [options.historicalTrend] - Optional 7-month trend data
 * @param {String} [options.tier]       - Override: 'standard' | 'premium'
 * @returns {Promise<jsPDF>}
 */
export const generateLedgerReport = async ({
  client,
  period,
  transactions,
  historicalTrend = [],
  tier = null,
}) => {
  // Resolve tier: explicit override > client config > default
  const resolvedTier = tier
    || client?.report_tier
    || 'standard';

  const reportData = {
    report_id: buildReportId(client, period),
    period_start: period.start,
    period_end: period.end,
    generated_at: new Date().toISOString(),
    prepared_for: client?.name || 'Internal',
    drilldown_url: buildDrilldownUrl(client, period),
    transactions,
    historical_trend: historicalTrend,
  };

  if (resolvedTier === 'premium') {
    return await generatePremiumLedger(reportData);
  }
  return await generateStandardLedger(reportData);
};

/**
 * Save the generated PDF to user's device.
 */
export const downloadLedgerReport = async (options) => {
  const doc = await generateLedgerReport(options);
  const filename = buildFilename(options.client, options.period, options.tier);
  doc.save(filename);
};

/**
 * Get the PDF as a Blob (for upload to Supabase storage, email attachment, etc.)
 */
export const getLedgerBlob = async (options) => {
  const doc = await generateLedgerReport(options);
  return doc.output('blob');
};

/**
 * Get the PDF as a base64 data URI (for inline preview).
 */
export const getLedgerDataUri = async (options) => {
  const doc = await generateLedgerReport(options);
  return doc.output('datauristring');
};

// ============================================================
//  HELPERS
// ============================================================

const buildReportId = (client, period) => {
  const d = new Date(period.start);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const clientCode = client?.code || '0000';
  return `DE/ADV/${yyyy}-${mm}/${clientCode}`;
};

const buildDrilldownUrl = (client, period) => {
  const d = new Date(period.start);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `app.developmentexpress.in/reports/adv/${yyyy}-${mm}/${client?.id || 'all'}`;
};

const buildFilename = (client, period, tier) => {
  const d = new Date(period.start);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const clientPart = client?.name
    ? client.name.replace(/[^a-zA-Z0-9]+/g, '_').substring(0, 30)
    : 'All_Clients';
  const tierPart = tier === 'premium' ? 'PREMIUM' : 'Standard';
  return `Advance_Ledger_${tierPart}_${clientPart}_${yyyy}-${mm}.pdf`;
};

// Re-export for direct use if needed
export { generateStandardLedger, generatePremiumLedger };
export { generatePremiumOwnerReceipt } from './generatePremiumOwnerReceipt';
export { generateOwnerPayoutLedgerPDF } from './generateOwnerPayoutLedgerPDF';
export { generatePremiumClientInvoice } from './generatePremiumClientInvoice';
export { generatePremiumClientBookingReport } from './generatePremiumClientBookingReport';
export {
  mapWalletTransactionsToLedger,
  buildClientLookup,
  buildBookingRefLookup,
  buildMonthlyTrendFromLedger,
  inferWalletTxnChannel,
  inferWalletTxnStatus,
} from './mapWalletTransactions';
export { PdfLayoutEngine, TABLE_METRICS } from './pdfLayoutEngine';
